import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Shield, UserCheck, UserX, Eye, Edit } from "lucide-react";
import { api } from "@/api/client";
import { useAuthStore } from "@/store/authSlice";
import { roleLabel } from "@/hooks/useRBAC";

interface UserWithRoles {
  id: string;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
  roles: Array<{ id: string; name: string }>;
}

interface UserListResponse {
  items: UserWithRoles[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const ROLE_COLORS: Record<string, string> = {
  ADMIN: "bg-red-100 text-red-700",
  UPLOADER: "bg-blue-100 text-blue-700",
  LEADER: "bg-purple-100 text-purple-700",
  AUDITOR: "bg-yellow-100 text-yellow-700",
  MANAGER: "bg-blue-100 text-blue-700",
  TREASURER: "bg-green-100 text-green-700",
  USER: "bg-gray-100 text-gray-700",
  GUEST: "bg-slate-100 text-slate-600",
};

export default function UserListPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.user_id);

  const { data, isLoading } = useQuery<UserListResponse>({
    queryKey: ["users", page, search],
    queryFn: () => api.get("/users/", { page, page_size: 20, search: search || undefined }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      api.put(`/users/${userId}`, { is_active: isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manajemen Pengguna</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {data?.total ?? 0} pengguna terdaftar
          </p>
        </div>
        <Link to="/users/create" className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          <Plus className="h-4 w-4" />
          Tambah Pengguna
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Cari berdasarkan username atau email..."
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pengguna</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Bergabung</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.items ?? []).map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <Link to={`/users/${user.id}`} className="font-medium text-blue-600 hover:text-blue-700">
                          {user.full_name}
                        </Link>
                        <p className="text-xs text-gray-400">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role.id}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[role.name] ?? "bg-gray-100 text-gray-700"}`}
                        >
                          <Shield className="h-3 w-3" />
                          {roleLabel(role.name)}
                        </span>
                      ))}
                      {user.roles.length === 0 && (
                        <span className="text-xs text-gray-400">Tidak ada role</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {user.is_active ? (
                        <><UserCheck className="h-3 w-3" /> Aktif</>
                      ) : (
                        <><UserX className="h-3 w-3" /> Nonaktif</>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {new Date(user.created_at).toLocaleDateString("id-ID")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/users/${user.id}`}
                        className="rounded p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        title="Detail"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/users/${user.id}/edit`}
                        className="rounded p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      {user.id !== currentUserId && (
                        <button
                          onClick={() => toggleActiveMutation.mutate({ userId: user.id, isActive: !user.is_active })}
                          className={`text-xs px-2.5 py-1 rounded-lg border ${user.is_active ? "border-red-200 text-red-600 hover:bg-red-50" : "border-green-200 text-green-600 hover:bg-green-50"}`}
                        >
                          {user.is_active ? "Nonaktifkan" : "Aktifkan"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Tidak ada pengguna ditemukan
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-500">Halaman {page} dari {data.total_pages}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
            >
              Sebelumnya
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              disabled={page === data.total_pages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
            >
              Berikutnya
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
